import { useState, useEffect, useCallback, useRef } from 'react';
import type { TimelineFrame } from '../hooks/useDrawingState';
import { FrameThumbnail } from './FrameThumbnail';

const MAX_HOLD = 4;
const HOLD_UNIT_PX = 48;
const THUMBNAIL_HEIGHT = 34;
const ADD_BTN_WIDTH = 42;
const FRAMES_PADDING = 12;

interface AnimationControlsProps {
  frames: TimelineFrame[];
  currentFrameIndex: number;
  fps: number;
  isPreviewing: boolean;
  maxFrames: number;
  onGoToFrame: (index: number) => void;
  onAddFrame: () => void;
  onDeleteFrame: (index: number) => void;
  onReorderFrames: (fromIndex: number, toIndex: number) => void;
  onResizeFrame: (index: number, newHold: number) => void;
  onSetFps: (fps: number) => void;
  onTogglePreview: () => void;
}

const PlayIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5,3 19,12 5,21" />
  </svg>
);

const StopIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <rect x="4" y="4" width="16" height="16" rx="2" />
  </svg>
);

const SmallTrashIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

export function AnimationControls({
  frames,
  currentFrameIndex,
  fps,
  isPreviewing,
  maxFrames,
  onGoToFrame,
  onAddFrame,
  onDeleteFrame,
  onReorderFrames,
  onResizeFrame,
  onSetFps,
  onTogglePreview,
}: AnimationControlsProps) {
  const framesContainerRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [resizing, setResizing] = useState<{
    index: number;
    startX: number;
    startHold: number;
    currentWidth: number;
    maxWidth: number;
  } | null>(null);

  useEffect(() => {
    if (!resizing) return;

    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - resizing.startX;
      const raw = resizing.startHold * HOLD_UNIT_PX + dx;
      const clamped = Math.max(HOLD_UNIT_PX, Math.min(resizing.maxWidth, raw));
      setResizing((prev) => (prev ? { ...prev, currentWidth: clamped } : null));
    };

    const onUp = () => {
      if (resizing) {
        const newHold = Math.max(1, Math.min(MAX_HOLD, Math.round(resizing.currentWidth / HOLD_UNIT_PX)));
        onResizeFrame(resizing.index, newHold);
      }
      setResizing(null);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [resizing, onResizeFrame]);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent, index: number) => {
      e.stopPropagation();
      e.preventDefault();
      const containerWidth = framesContainerRef.current?.offsetWidth ?? 480;
      const othersMinWidth = frames
        .filter((_, i) => i !== index)
        .reduce((sum) => sum + HOLD_UNIT_PX, 0);
      const gaps = Math.max(0, frames.length - 1) * 2;
      const available = containerWidth - FRAMES_PADDING - ADD_BTN_WIDTH - othersMinWidth - gaps;
      const maxWidth = Math.min(HOLD_UNIT_PX * MAX_HOLD, Math.max(HOLD_UNIT_PX, available));
      setResizing({
        index,
        startX: e.clientX,
        startHold: frames[index].hold,
        currentWidth: frames[index].hold * HOLD_UNIT_PX,
        maxWidth,
      });
    },
    [frames],
  );

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault();
      if (dragIndex !== null && dragIndex !== toIndex) onReorderFrames(dragIndex, toIndex);
      setDragIndex(null);
    },
    [dragIndex, onReorderFrames],
  );

  const handleDragEnd = useCallback(() => setDragIndex(null), []);

  const getFrameWidth = (frame: TimelineFrame, index: number) =>
    resizing?.index === index ? resizing.currentWidth : frame.hold * HOLD_UNIT_PX;

  return (
    <div className="timeline-bar">
      <div className="timeline-bar__header">
        <span className="timeline-bar__label">Timeline</span>
        <div className="timeline-bar__controls">
          <select
            className="timeline-bar__fps-select"
            value={fps}
            onChange={(e) => onSetFps(Number(e.target.value))}
          >
            <option value={1}>1fps</option>
            <option value={2}>2fps</option>
            <option value={3}>3fps</option>
            <option value={4}>4fps</option>
          </select>
          <button
            className={`timeline-bar__btn ${isPreviewing ? 'timeline-bar__btn--active' : ''}`}
            onClick={onTogglePreview}
            title={isPreviewing ? 'Stop preview' : 'Preview'}
          >
            {isPreviewing ? <StopIcon /> : <PlayIcon />}
          </button>
        </div>
      </div>

      <div className="timeline-bar__separator" />

      <div className="timeline-bar__frames" ref={framesContainerRef}>
        {frames.map((frame, i) => {
          const isSelected = i === currentFrameIndex;
          const frameWidth = getFrameWidth(frame, i);
          return (
            <div
              key={frame.id}
              className={`timeline-bar__frame ${isSelected ? 'timeline-bar__frame--selected' : ''} ${dragIndex === i ? 'timeline-bar__frame--dragging' : ''}`}
              style={{ width: frameWidth }}
              onClick={() => onGoToFrame(i)}
              draggable={!resizing}
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
            >
              {isSelected && <div className="timeline-bar__playhead" />}
              <FrameThumbnail
                strokes={frame.strokes}
                width={Math.max(32, frameWidth - 4)}
                height={THUMBNAIL_HEIGHT}
              />
              {isSelected && (
                <button
                  className="timeline-bar__frame-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFrame(i);
                  }}
                  title="Delete frame"
                >
                  <SmallTrashIcon />
                </button>
              )}
              <div
                className="timeline-bar__frame-resize"
                onPointerDown={(e) => handleResizeStart(e, i)}
              />
            </div>
          );
        })}
        <button
          className="timeline-bar__add-frame"
          onClick={onAddFrame}
          disabled={frames.length >= maxFrames}
          title="Add frame"
        >
          +
        </button>
      </div>
    </div>
  );
}
